import { useAtom } from 'jotai/index';
import { useEffect, useRef } from 'react';
import useInputState from './UseInputState';
import {
  isPartnersInputsDirtyAtom,
  partnerSaveAtom,
  selectedPartnerAtom,
} from '../atoms/partnersAtoms';
import PartnersServices from '../services/PartnersServices';

const useSinglePartnerSettings = ({ savePartner }) => {
  const [isSmsEnabled, setIsSmsEnabled, isSmsEnabledError, setIsSmsEnabledError] = useInputState();
  const [smsSenderName, setSmsSenderName, smsSenderNameError, setSmsSenderNameError] = useInputState();
  const [sendThroughTelegramBot, setSendThroughTelegramBot, sendThroughTelegramBotError, setSendThroughTelegramBotError] = useInputState();
  const [telegramBotName, setTelegramBotName, telegramBotNameError, setTelegramBotNameError] = useInputState();
  const [onlineReservationExtendedMode, setOnlineReservationExtendedMode, onlineReservationExtendedModeError, setOnlineReservationExtendedModeError] = useInputState();

  const [partnerSave, setPartnerSave] = useAtom(partnerSaveAtom);
  const [selectedPartner] = useAtom(selectedPartnerAtom);
  const [, setIsPartnersInputsDirty] = useAtom(isPartnersInputsDirtyAtom);
  const isInitialMountRef = useRef(true);

  // const checkForErrors = () => {
  //   let isError = false;
  //   if (!isSmsEnabled) {
  //     setIsSmsEnabledError('Укажите статус');
  //     isError = true;
  //   }
  //   if (!smsSenderName) {
  //     setSmsSenderNameError('Укажите имя отправителя');
  //     isError = true;
  //   }

  //   return isError;
  // };

  const handleSavePartner = () => {
    // const isError = checkForErrors();
    // if (isError) return;

    savePartner({
      ...selectedPartner,
      settings: {
        sms_enabled: isSmsEnabled?.value === 'active',
        sms_sender_name: smsSenderName,
        send_through_telegram_bot: sendThroughTelegramBot?.value === 'active',
        telegram_bot_name: telegramBotName || '',
        online_reservation_extended_mode: onlineReservationExtendedMode?.value === 'active',
      }
    });
  };

  useEffect(() => {
    if (partnerSave) {
      handleSavePartner();
      setPartnerSave(0);
    }
  }, [partnerSave]);

  useEffect(() => {
    // Пропускаем проверку при первом монтировании, чтобы избежать ложных срабатываний
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false;
      return;
    }

    if (!selectedPartner) {
      setIsPartnersInputsDirty(false);
      return;
    }

    // Пропускаем проверку, если значения еще не инициализированы
    if (!isSmsEnabled || !onlineReservationExtendedMode || !onlineReservationExtendedMode?.value || sendThroughTelegramBot === undefined) {
      return;
    }

    // checking if inputs were changed
    // Нормализуем значения для сравнения (учитываем null/undefined)
    const currentSmsEnabled = isSmsEnabled?.value === 'active';
    const originalSmsEnabled = Boolean(selectedPartner.settings?.sms_enabled);
    const currentSmsSenderName = (smsSenderName || '').trim();
    const originalSmsSenderName = (selectedPartner.settings?.sms_sender_name || '').trim();
    const currentSendThroughTelegram = sendThroughTelegramBot?.value === 'active';
    const originalSendThroughTelegram = Boolean(selectedPartner.settings?.send_through_telegram_bot);
    const currentTelegramBotName = (telegramBotName || '').trim();
    const originalTelegramBotName = (selectedPartner.settings?.telegram_bot_name || '').trim();
    const currentOnlineReservationExtended = onlineReservationExtendedMode?.value === 'active';
    const originalOnlineReservationExtended = Boolean(selectedPartner.settings?.online_reservation_extended_mode);

    const isDirty = currentSmsEnabled !== originalSmsEnabled
      || currentSmsSenderName !== originalSmsSenderName
      || currentSendThroughTelegram !== originalSendThroughTelegram
      || currentTelegramBotName !== originalTelegramBotName
      || currentOnlineReservationExtended !== originalOnlineReservationExtended;

    setIsPartnersInputsDirty(isDirty);
  }, [
    selectedPartner,
    isSmsEnabled,
    smsSenderName,
    sendThroughTelegramBot,
    telegramBotName,
    onlineReservationExtendedMode,
    setIsPartnersInputsDirty,
  ]);

  useEffect(() => {
    if (!selectedPartner) {
      isInitialMountRef.current = true;
      return;
    }
    
    // Сбрасываем флаг при загрузке новых данных настроек
    isInitialMountRef.current = true;
    setIsPartnersInputsDirty(false);
    
    setIsSmsEnabled(selectedPartner.settings?.sms_enabled ? { value: 'active', label: 'Включено' } : { value: 'nonActive', label: 'Выключено' }, true);
    setSmsSenderName(selectedPartner.settings?.sms_sender_name || '', true);
    setSendThroughTelegramBot(selectedPartner.settings?.send_through_telegram_bot ? { value: 'active', label: 'Включено' } : { value: 'nonActive', label: 'Выключено' }, true);
    setTelegramBotName(selectedPartner.settings?.telegram_bot_name || '', true);
    setOnlineReservationExtendedMode(
      selectedPartner.settings?.online_reservation_extended_mode
        ? { value: 'active', label: 'Расширенный' }
        : { value: 'nonActive', label: 'Стандартный' },
      true,
    );
  }, [selectedPartner, setIsPartnersInputsDirty]);

  return {
    values: {
      isSmsEnabled,
      smsSenderName,
      sendThroughTelegramBot,
      telegramBotName,
      onlineReservationExtendedMode,
    },
    setters: {
      isSmsEnabled: setIsSmsEnabled,
      smsSenderName: setSmsSenderName,
      sendThroughTelegramBot: setSendThroughTelegramBot,
      telegramBotName: setTelegramBotName,
      onlineReservationExtendedMode: setOnlineReservationExtendedMode,
    },
    errors: {
      isSmsEnabled: isSmsEnabledError,
      smsSenderName: smsSenderNameError,
      sendThroughTelegramBot: sendThroughTelegramBotError,
      telegramBotName: telegramBotNameError,
      onlineReservationExtendedMode: onlineReservationExtendedModeError,
    },
  };
};

export default useSinglePartnerSettings;
